import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { colors } from '../../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../../constants/text';
import StarRating from '../../../../../components/SolvPatternLibrary/StarRating';
import Button, { ButtonVariations } from '../../../../../components/SolvPatternLibrary/Button';
import BadgeVerifiedPatient from '../../../../../components/SolvPatternLibrary/Icons/BadgeVerifiedPatient';
import ReportFlag from '../../../../../components/SolvPatternLibrary/Icons/ReportFlag';
import HelpfulButton from './HelpfulButton';
import { smallerThan } from '../../../../../core/util/styledComponents';
import Collapse from '@material-ui/core/Collapse';
import moment from 'moment-timezone';
import { analyticsTrackEvent } from '../../../../../core/analytics';
import {
  CDP_REVIEWS_TOUCH_REPORT,
  CDP_REVIEWS_TOUCH_SHOW_LESS,
  CDP_REVIEWS_TOUCH_SHOW_MORE,
} from '../../../../../core/analytics/events';
import { connect } from 'react-redux';
import { flagSolvRating } from '../../../../../ducks/solvRatings';
import { REPORT_REVIEW_MODAL } from '../../../Modals/ReportReviewModal';
import { SOLV_AUTHOR_NAME } from '../../../../../constants';

const Root = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.fatherJohnMisty};
  padding: 24px 0;

  ${smallerThan.sm`
    display: block;
  `}
`;

const ReviewedDate = styled.span`
  font-family: ${fontFamily.book};
  font-size: ${fontSize.small};
  color: ${colors.lanaDelGrey};
  margin-top: 4px;

  ${smallerThan.sm`
    margin-left: 16px;
    margin-top: 0;
  `}
`;

const RatingAndDateContainer = styled.div`
  ${smallerThan.sm`
    display: flex;
    align-items: center;
  `}
`;

const Description = styled.div`
  font-size: ${fontSize.mediumSmall};
  color: ${colors.squid};
  max-width: 500px;
`;

const StyledButton = styled(Button)`
  height: 36px;
  width: 100%;
  font-size: ${fontSize.small};
  padding: 0px;
  margin-top: 20px;
  color: ${colors.squid};
  flex: 0 1 120px;
`;

const ReportLink = styled(StyledButton)`
  text-transform: none;
`;

const LeftRail = styled.div`
  flex: 0 1 240px;
  display: flex;
  flex-direction: column;
`;

const RightRail = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;

  ${smallerThan.sm`
    margin-top: 16px;
  `}
`;

const ReportLinkContent = styled.div`
  width: 82%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Ctas = styled.div`
  display: flex;
`;

const StyledReportFlag = styled(ReportFlag)`
  width: 13px;
`;

const StyledBadgeVerifiedPatient = styled(BadgeVerifiedPatient)`
  height: 28px;
  width: 113px;
  margin-top: 12px;
`;

const StyledCollapse = styled(Collapse)`
  ${props =>
    !props.in &&
    css`
      ::before {
        position: absolute;
        height: ${props.collapsedHeight};
        width: 100%;
        content: '';
        background: linear-gradient(to top, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 50%);
        pointer-events: none; /* so the text is still selectable */
      }
    `}
`;

const MoreOrLess = styled.span`
  color: ${colors.link};
  font-size: ${fontSize.mediumSmall};
  cursor: pointer;
`;

class ReviewItem extends PureComponent {
  static propTypes = {
    rating: PropTypes.number,
    createdDate: PropTypes.string,
    description: PropTypes.string,
    helpfulCount: PropTypes.object,
    shouldShowVerified: PropTypes.bool,
    ratingId: PropTypes.string,
    flagSolvRating: PropTypes.func,
    showModal: PropTypes.func,
    locationTimezone: PropTypes.string,
  };

  state = { expanded: false };

  handleClickMoreOrLess = () => {
    const event = this.state.expanded ? CDP_REVIEWS_TOUCH_SHOW_LESS : CDP_REVIEWS_TOUCH_SHOW_MORE;

    analyticsTrackEvent(event, { solvRatingId: this.props.ratingId });
    this.setState(state => ({ expanded: !state.expanded }));
  };

  handleClickReport = () => {
    analyticsTrackEvent(CDP_REVIEWS_TOUCH_REPORT, {
      solvRatingId: this.props.ratingId,
    });
    this.props.flagSolvRating(this.props.ratingId);
    this.props.showModal(REPORT_REVIEW_MODAL);
  };

  render = () => {
    const {
      rating,
      createdDate,
      description,
      helpfulCount,
      shouldShowVerified,
      ratingId,
      locationTimezone,
    } = this.props;

    const { expanded } = this.state;
    const createdDateFormatted = moment.tz(createdDate, locationTimezone).format('MMM YYYY');
    const createdDateFormattedForSchema = moment
      .tz(createdDate, locationTimezone)
      .format('YYYY-MM-DD');

    return (
      <Root itemProp="review" itemScope itemType="https://schema.org/Review">
        <meta itemProp="author" content={SOLV_AUTHOR_NAME} />
        <LeftRail>
          <RatingAndDateContainer>
            <span itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
              <meta itemProp="ratingValue" content={rating} />
            </span>
            <StarRating identifier={ratingId} rating={rating} size={14} />
            <ReviewedDate>
              Reviewed in{' '}
              <time itemProp="dateCreated" dateTime={createdDateFormattedForSchema}>
                {createdDateFormatted}
              </time>
            </ReviewedDate>
          </RatingAndDateContainer>
          {shouldShowVerified && <StyledBadgeVerifiedPatient />}
        </LeftRail>
        <RightRail>
          {description.length > 500 ? (
            <Fragment>
              <StyledCollapse
                in={expanded}
                collapsedHeight="100px"
                timeout={{ enter: 250, exit: 0 }}
              >
                <Description itemProp="reviewBody">{description}</Description>
              </StyledCollapse>
              <MoreOrLess onClick={this.handleClickMoreOrLess}>
                {expanded ? 'Show Less' : 'Read More'}
              </MoreOrLess>
            </Fragment>
          ) : (
            <Description itemProp="reviewBody">{description}</Description>
          )}
          <Ctas>
            <HelpfulButton helpfulCount={helpfulCount} solvRatingId={ratingId} />
            <ReportLink variation={ButtonVariations.link} onClick={this.handleClickReport}>
              <ReportLinkContent>
                <StyledReportFlag />
                <span>Report Review</span>
              </ReportLinkContent>
            </ReportLink>
          </Ctas>
        </RightRail>
      </Root>
    );
  };
}

const mapDispatchToProps = dispatch => ({
  flagSolvRating: solvRatingId => dispatch(flagSolvRating(solvRatingId, true)),
});

export default connect(
  null,
  mapDispatchToProps
)(ReviewItem);
